import React from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HubIcon from '@mui/icons-material/Hub';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import ListAltIcon from '@mui/icons-material/ListAlt';
import PaletteIcon from '@mui/icons-material/Palette';
import PublicIcon from '@mui/icons-material/Public';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimelineIcon from '@mui/icons-material/Timeline';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext';
import { useFeatureFlags } from '../../features/FeatureFlagsContext';
import { canonicalRequest } from './CanonicalEditorCommon';
import { formatDate, labelize, StatusChip } from '../../components/workspace/WorkspaceStatus';
import WorkspacePageHeader from '../../components/workspace/WorkspacePageHeader';

const initialDashboard = {
  products: null,
  listings: null,
  publications: null,
  connections: null,
  draftProducts: null,
  readyListings: null,
  attention: null,
  activity: null,
};

const dashboardRequests = {
  products: '/products?limit=1',
  listings: '/listings?limit=1',
  publications: '/publications?limit=1',
  connections: '/connections?limit=100',
  draftProducts: '/products?limit=1&status=draft',
  readyListings: '/listings?limit=1&status=ready',
  attention: '/activity?limit=6&status=needs_attention',
  activity: '/activity?limit=6',
};

function countOf(page) {
  return typeof page?.total === 'number' ? page.total : null;
}

function MetricCard({ label, value, helper, icon, to }) {
  const navigate = useNavigate();
  return (
    <Card variant="outlined" sx={{ height: '100%' }}>
      <CardActionArea onClick={() => navigate(to)} sx={{ height: '100%', textAlign: 'left' }}>
        <CardContent sx={{ p: 2.25 }}>
          <Stack direction="row" justifyContent="space-between" spacing={2} alignItems="flex-start">
            <Box>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
              {value === null ? <Skeleton width={72} height={42} /> : <Typography variant="h3" sx={{ mt: 0.25 }}>{value.toLocaleString()}</Typography>}
              <Typography variant="caption" color="text.secondary">{helper}</Typography>
            </Box>
            <Box
              aria-hidden="true"
              sx={{ width: 42, height: 42, borderRadius: 3, bgcolor: 'var(--md-sys-color-primary-container)', color: 'primary.main', display: 'grid', placeItems: 'center' }}
            >
              {icon}
            </Box>
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
}

function WorkCard({ title, count, description, to, actionLabel }) {
  const navigate = useNavigate();
  return (
    <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
      <Stack spacing={1.25} sx={{ height: '100%' }}>
        <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
          <Typography variant="subtitle1">{title}</Typography>
          {count === null ? <CircularProgress size={18} /> : <Chip size="small" label={count.toLocaleString()} />}
        </Stack>
        <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>{description}</Typography>
        <Button endIcon={<ArrowForwardIcon />} onClick={() => navigate(to)} sx={{ alignSelf: 'flex-start' }}>
          {actionLabel}
        </Button>
      </Stack>
    </Paper>
  );
}

function ActivityRow({ item }) {
  const navigate = useNavigate();
  return (
    <Box
      component="button"
      type="button"
      onClick={() => navigate(`/activity?kind=${encodeURIComponent(item.kind || '')}&q=${encodeURIComponent(item.id || '')}`)}
      sx={{
        width: '100%', border: 0, bgcolor: 'transparent', color: 'inherit', textAlign: 'left', p: 1.25,
        borderRadius: 2, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' }, '&:focus-visible': { boxShadow: '0 0 0 3px rgba(0, 107, 94, 0.28)' },
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center">
        <Box aria-hidden="true" sx={{ width: 34, height: 34, borderRadius: '50%', bgcolor: 'var(--md-sys-color-surface-container-high)', display: 'grid', placeItems: 'center', flexShrink: 0 }}>
          <TimelineIcon fontSize="small" />
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="body2" noWrap>{labelize(item.action)}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
            {labelize(item.provider || item.kind)} · {formatDate(item.updated_at)}
          </Typography>
        </Box>
        <StatusChip status={item.status} />
      </Stack>
    </Box>
  );
}

const UnifiedHomePage = () => {
  const navigate = useNavigate();
  const { fetchWithAuth, username } = useAuth();
  const { flags } = useFeatureFlags();
  const [dashboard, setDashboard] = React.useState(initialDashboard);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');

  const loadDashboard = React.useCallback(async () => {
    setLoading(true);
    setError('');
    const entries = Object.entries(dashboardRequests);
    const settled = await Promise.allSettled(entries.map(([, path]) => canonicalRequest(fetchWithAuth, path)));
    const next = { ...initialDashboard };
    let failures = 0;
    settled.forEach((result, index) => {
      const [key] = entries[index];
      if (result.status === 'fulfilled') {
        next[key] = result.value;
      } else {
        failures += 1;
      }
    });
    setDashboard(next);
    if (failures === entries.length) {
      setError('The workspace summary could not be loaded. Check the API connection and try again.');
    } else if (failures > 0) {
      setError('Some workspace summaries are temporarily unavailable. Available information is shown below.');
    }
    setLoading(false);
  }, [fetchWithAuth]);

  React.useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const connections = dashboard.connections?.items || [];
  const unhealthyConnections = connections.filter((connection) => connection.active && connection.status !== 'active');
  const attentionItems = dashboard.attention?.items || [];
  const recentActivity = dashboard.activity?.items || [];
  const displayName = String(username || '').split('@')[0].trim();

  return (
    <Stack spacing={3} sx={{ pb: 4 }}>
      <WorkspacePageHeader
        eyebrow="Workspace overview"
        title={displayName ? `Welcome back, ${displayName}` : 'Welcome to PODBoss'}
        description="Build products, publish listings, and keep every sales channel healthy from one place."
        actions={(
          <>
          <Button variant="outlined" startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />} onClick={loadDashboard} disabled={loading}>
            Refresh
          </Button>
          {flags.unified_writes ? (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/launch')}>Launch a product</Button>
          ) : null}
          </>
        )}
      />

      {error ? <Alert severity={dashboard.products ? 'warning' : 'error'}>{error}</Alert> : null}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', xl: 'repeat(4, minmax(0, 1fr))' }, gap: 2 }}>
        <MetricCard label="Products" value={countOf(dashboard.products)} helper="Canonical product concepts" icon={<Inventory2Icon />} to="/products" />
        <MetricCard label="Listings" value={countOf(dashboard.listings)} helper="Reusable merchandising drafts" icon={<ListAltIcon />} to="/listings" />
        <MetricCard label="Publications" value={countOf(dashboard.publications)} helper="Sales-channel destinations" icon={<PublicIcon />} to="/listings" />
        <MetricCard label="Connections" value={countOf(dashboard.connections)} helper="Fulfillment and sales channels" icon={<HubIcon />} to="/connections" />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(2, minmax(0, 1fr))' }, gap: 2 }}>
        <WorkCard
          title="Draft products"
          count={countOf(dashboard.draftProducts)}
          description="Finish artwork, variants, and fulfillment setup before preparing listings."
          to="/products?status=draft"
          actionLabel="Open drafts"
        />
        <WorkCard
          title="Listings ready to publish"
          count={countOf(dashboard.readyListings)}
          description="Review destinations and preview an immutable publication plan before publishing."
          to="/listings?status=ready"
          actionLabel="Review listings"
        />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', xl: 'minmax(0, 0.85fr) minmax(0, 1.15fr)' }, gap: 2 }}>
        <Paper variant="outlined" sx={{ p: 2.25 }}>
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
              <Box>
                <Typography variant="subtitle1">Needs attention</Typography>
                <Typography variant="body2" color="text.secondary">Connection and workflow problems that may block publishing.</Typography>
              </Box>
              <ErrorOutlineIcon color={unhealthyConnections.length || attentionItems.length ? 'warning' : 'disabled'} />
            </Stack>
            <Divider />
            {unhealthyConnections.length === 0 && attentionItems.length === 0 ? (
              <Stack direction="row" spacing={1.25} alignItems="center" sx={{ py: 2 }}>
                <CheckCircleOutlineIcon color="success" />
                <Box>
                  <Typography variant="body2">Nothing needs attention.</Typography>
                  <Typography variant="caption" color="text.secondary">Your visible connections and recent workflows look healthy.</Typography>
                </Box>
              </Stack>
            ) : (
              <Stack spacing={1}>
                {unhealthyConnections.slice(0, 3).map((connection) => (
                  <Button key={connection.id} color="inherit" onClick={() => navigate(`/connections?q=${connection.id}`)} sx={{ justifyContent: 'space-between', textAlign: 'left' }}>
                    <span>{connection.display_name || labelize(connection.provider)}</span>
                    <StatusChip status={connection.status} />
                  </Button>
                ))}
                {attentionItems.slice(0, 3).map((item) => <ActivityRow key={`${item.kind}-${item.id}`} item={item} />)}
              </Stack>
            )}
            <Button endIcon={<ArrowForwardIcon />} onClick={() => navigate('/activity?status=needs_attention')} sx={{ alignSelf: 'flex-start' }}>Open Activity</Button>
          </Stack>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.25 }}>
          <Stack spacing={1.25}>
            <Stack direction="row" justifyContent="space-between" spacing={1} alignItems="center">
              <Box>
                <Typography variant="subtitle1">Recent activity</Typography>
                <Typography variant="body2" color="text.secondary">Durable publication workflows and provider operations.</Typography>
              </Box>
              <Button size="small" onClick={() => navigate('/activity')}>View all</Button>
            </Stack>
            <Divider />
            {loading && recentActivity.length === 0 ? (
              <Stack spacing={1}>{[0, 1, 2].map((item) => <Skeleton key={item} variant="rounded" height={56} />)}</Stack>
            ) : recentActivity.length ? recentActivity.map((item) => <ActivityRow key={`${item.kind}-${item.id}`} item={item} />) : (
              <Box sx={{ py: 3, textAlign: 'center' }}>
                <TimelineIcon color="disabled" />
                <Typography variant="body2" sx={{ mt: 1 }}>No activity has been recorded yet.</Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      </Box>

      {flags.unified_writes ? (
        <Paper variant="outlined" sx={{ p: 2.25 }}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ md: 'center' }}>
            <Box>
              <Typography variant="subtitle1">Start something new</Typography>
              <Typography variant="body2" color="text.secondary">Create the reusable intent first; publishing remains a separate, reviewable action.</Typography>
            </Box>
            <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
              <Button variant="outlined" startIcon={<PaletteIcon />} onClick={() => navigate('/designs/new')}>New design</Button>
              <Button variant="contained" startIcon={<Inventory2Icon />} onClick={() => navigate('/launch')}>Launch a product</Button>
              <Button variant="outlined" startIcon={<Inventory2Icon />} onClick={() => navigate('/products/new')}>Product draft</Button>
              <Button variant="outlined" startIcon={<ListAltIcon />} onClick={() => navigate('/listings/new')}>New listing</Button>
            </Stack>
          </Stack>
        </Paper>
      ) : null}
    </Stack>
  );
};

export default UnifiedHomePage;
